/** @module components/zoomable-region */
import _ from "underscore";
import baustein from "baustein";
import browser from "web/script/utils/browser";
import events from "web/script/dom/events";
import style from "web/script/dom/style";

var MODE_NONE = 0;
var MODE_PAN = 1;
var MODE_ZOOM = 2;

var EVENT_ZOOM_START = "zoomStateStart";
var EVENT_ZOOM_END = "zoomStateEnd";

/**
 * @constructor
 * @alias module:components/zoomable-element
 * @extends module:components/components
 */
export default baustein.register(
    "zoomable-element",
    /** @lends module:components/zoomable-element.prototype */
    {
        defaultOptions: {
            maxZoom: 6,
        },

        _startTouches: null,

        /**
         * @type HTMLElement
         */
        _zoomObject: null,
        /**
         * These bounds are noted the first time the zoom object is captured. These are
         * typically the bounds that the object starts at, and the object can go no smaller
         * or beyond them.
         * @type ClientRect
         */
        _baseBounds: null,

        /**
         * These are the bounds noted when the action has begun (i.e. the fingers touch the
         * screen). This is because the bounds change as the user zooms in/out.
         * @type ClientRect
         */
        _startBounds: null,

        /**
         * This stores the zoom level when the action has begun (i.e. the fingers touch the
         * screen).
         * @type number
         */
        _startZoom: null,

        /**
         * Keeps track of the current movement mode, either MODE_PAN or MODE_ZOOM.
         * @type number
         */
        _mode: MODE_NONE,

        /**
         * A flag to indicate if the image is currently zoomed in or not.
         * @type boolean
         */
        _isZoomed: false,

        /**
         * A flag to indicate if actions are prevented from being performed.
         * @type boolean
         */
        _blocked: false,

        /**
         * Gets the current zoom scale of the object. If there is currently no object zoomed
         * or panned, then it will return 0.
         * @returns {number}
         */
        getCurrentScale: function() {
            if (!this._zoomObject) {
                return 0;
            }
            var currentBounds = this._getPositionRelativeToParent();
            return (currentBounds.width - this._baseBounds.width) / this._baseBounds.width + 1;
        },

        setupEvents: function(add) {
            add("touchstart", this._onTouchStart);
        },

        onDestroy: function() {
            this._zoomObject = null;
        },

        block: function() {
            this._blocked = true;
        },

        unblock: function() {
            this._blocked = false;
        },

        _onTouchStart: function(event) {
            if (event.defaultPrevented) {
                return;
            }

            // capture a reference to the object, we don't want to be constantly polling
            // for it while the user is moving their finger around
            var zoomingObject = this.el.querySelector("[data-zoomable]");
            if (!zoomingObject) {
                // nothing to zoom
                return;
            }

            if (this._zoomObject !== zoomingObject) {
                this._baseBounds = style.getPosition(zoomingObject);
                this._zoomObject = zoomingObject;
            }
            var fromMode = this._mode;
            // check if we want to zoom, to pan, or neither.
            var touches = this._getFingerPositions(event);
            if (touches.length === 1 && this._isZoomed) {
                this._mode = MODE_PAN;
            } else if (touches.length === 2) {
                this._mode = MODE_ZOOM;
                this.emit(EVENT_ZOOM_START);
            } else {
                return;
            }

            event.preventDefault();
            event.stopPropagation();

            // attach additional information to the touches
            this._processTouches(touches);

            this._startTouches = touches;
            var bounds = this._getPositionRelativeToParent();
            this._startBounds = bounds;
            this._startScale = this.getCurrentScale();

            if (fromMode === MODE_NONE) {
                // set up the node for zooming, do this in the next tick
                requestAnimationFrame(
                    function() {
                        style.setStyles(this._zoomObject, {
                            top: bounds.top,
                            left: bounds.left,
                            width: bounds.width,
                            height: bounds.height,
                        });
                        style.setStyle(this._zoomObject, "transform", "translate3d(0,0,0)");

                        this._setTransformOrigin(bounds);

                        this.registerEvent("touchmove", this._onTouchMove);
                        this.registerEvent("touchend", this._onTouchEnd);
                    }.bind(this)
                );
            } else {
                requestAnimationFrame(this._setTransformOrigin.bind(this, bounds));
            }
        },

        _setTransformOrigin: function(bounds) {
            var xOrigin = this._startTouches.midpoint.x - bounds.left;
            var yOrigin = this._startTouches.midpoint.y - bounds.top;

            var transformValue = xOrigin + "px " + yOrigin + "px";
            style.setStyle(this._zoomObject, "transform-origin", transformValue);
        },

        _getFingerPositions: function(event) {
            var rawTouches = event.targetTouches;

            var touchPoints = {};
            for (var i = 0; i < rawTouches.length; i++) {
                var thisTouch = rawTouches[i];
                touchPoints[i] = {
                    x: thisTouch.screenX,
                    y: thisTouch.screenY,
                };
            }
            touchPoints.length = rawTouches.length;

            return touchPoints;
        },

        /**
         * Processes a touches group and attaches additional information.
         * distance, midpoint, difference
         * @param touches
         * @private
         */
        _processTouches: function(touches) {
            if (touches.length < 2) {
                touches.distance = 0;
                touches.midpoint = {
                    x: touches[0].x,
                    y: touches[0].y,
                };
                return touches;
            }

            var first = touches[0];
            var second = touches[1];
            // fuck yeah, pythagoras!
            var xDist = Math.pow(first.x - second.x, 2);
            var yDist = Math.pow(first.y - second.y, 2);

            touches.distance = Math.sqrt(xDist + yDist);

            touches.midpoint = {
                x: (first.x + second.x) / 2,
                y: (first.y + second.y) / 2,
            };

            return touches;
        },

        /**
         * Process the touches, and do the appropriate action.
         * @param event
         * @private
         */
        _onTouchMove: function(event) {
            if (event.defaultPrevented) {
                return;
            }

            if (this._mode !== MODE_NONE) {
                event.preventDefault();
            }
            var touches = this._getFingerPositions(event);
            this._processTouches(touches);

            if (this._mode === MODE_PAN) {
                this._doPanEvent(touches);
            } else if (this._mode === MODE_ZOOM) {
                this._doZoomEvent(touches);
            }
        },

        /**
         * The zoom event scales the element by the distance the user has moved their fingers.
         * It also pans.
         * @param touches
         * @private
         */
        _doZoomEvent: function(touches) {
            // calculate the scale difference
            var zoomScale =
                1 + (touches.distance - this._startTouches.distance) / this._startTouches.distance;

            // if this will scale up the image to be too large, then reset it to the maximum zoom
            if (zoomScale * this._startScale >= this.options.maxZoom) {
                var maxWidth = this._baseBounds.width * this.options.maxZoom;
                var curWidth = this._startBounds.width;

                zoomScale = 1 + (maxWidth - curWidth) / curWidth;
            }

            style.setStyle(
                this._zoomObject,
                "transform",
                style.createTransformString({
                    translate3d: this._getPanValues(touches),
                    scale3d: [zoomScale, zoomScale, 1],
                })
            );
        },

        /**
         * The pan event moves the element around.
         * @param touches
         * @private
         */
        _doPanEvent: function(touches) {
            style.setStyle(
                this._zoomObject,
                "transform",
                style.createTransformString({
                    translate3d: this._getPanValues(touches),
                })
            );
        },

        /**
         * Helps getting the panning values.
         * @param touches
         * @returns {number[]}
         * @private
         */
        _getPanValues: function(touches) {
            // we can pan based on the midpoint, only provided
            var touch = touches.midpoint || touches[0];
            var start = this._startTouches.midpoint || this._startTouches[0];

            var deltaX = touch.x - start.x;
            var deltaY = touch.y - start.y;

            return [deltaX + "px", deltaY + "px", 0];
        },

        /**
         * On release, set the final position and then remove all the handlers.
         * @param event
         * @private
         */
        _onTouchEnd: function(event) {
            if (this._mode === MODE_NONE) {
                return;
            }

            event.preventDefault();

            requestAnimationFrame(this._finalisePosition.bind(this));

            this.releaseEvent("touchmove");
            this.releaseEvent("touchend");
            this._mode = MODE_NONE;
            this._startTouches = null;
        },

        /**
         * Usually you'd detect if an image is too far outside of its bounds based on the parent,
         * if an image is a bit beyond the edge or something, you want to return it back to where
         * it should go. Unfortunately, 99% of the time, the object we are panning/zooming doesn't
         * share a similar size, orientation or aspect ratio.
         *
         * So this sets up a virtual boundary that takes into account the size and orientation
         * of the element that's within it. It never goes beyond the parent bounds, but can be
         * narrower. It will also flag if the element is too small, which is generally that both
         * the width and the height are smaller than how it started.
         * @returns
         * @private
         */
        _getElementBoundingBox: function() {
            var zoom = this._getPositionRelativeToParent();
            var parent = style.getPosition(this._zoomObject.parentElement);

            var width = zoom.width;
            var height = zoom.height;

            var pWidth = parent.width;
            var pHeight = parent.height;

            var wDiff = pWidth - width;
            var hDiff = pHeight - height;

            return {
                top: Math.max(Math.round(hDiff / 2), 0),
                bottom: Math.max(Math.round(hDiff / 2), 0),
                left: Math.max(Math.round(wDiff / 2), 0),
                right: Math.max(Math.round(wDiff / 2), 0),
                width: width,
                height: height,
                tooSmall: width < parent.width && height < parent.height,
            };
        },

        /**
         * This calculates a bounding rect based on the position inside the parent, not the page.
         * @param {Object} [bounds] - if provided, will base it relative to these bounds
         * @private
         */
        _getPositionRelativeToParent: function(bounds) {
            var zoom = bounds || style.getPosition(this._zoomObject);
            var parent = style.getPosition(this._zoomObject.parentElement);

            return {
                top: Math.round(zoom.top - parent.top),
                bottom: Math.round(parent.bottom - zoom.bottom),
                left: Math.round(zoom.left - parent.left),
                right: Math.round(zoom.right - parent.right),
                width: zoom.width,
                height: zoom.height,
            };
        },

        _finalisePosition: function() {
            // get the bounding box for the zooming element
            var boundingLimits = this._getElementBoundingBox();

            // get some position data
            var position = this._getPositionRelativeToParent();
            var basePosition = this._getPositionRelativeToParent(this._baseBounds);

            // calculate where this element should go
            var top =
                boundingLimits.top === 0 && position.top < 0 ? position.top : boundingLimits.top;
            // if the top bounding limit is the same as the parent (0), and the position of the element
            // is less than 0 (ie. beyond the top), then it's fine to use, otherwise, reset to where
            // the limit is for this element (it's not necessarily 0, especially for portrait photos).

            var left =
                boundingLimits.left === 0 && position.left < 0
                    ? position.left
                    : boundingLimits.left;

            var width = boundingLimits.width;
            var height = boundingLimits.height;

            if (boundingLimits.tooSmall) {
                // if it's indicated to be too small, then reset it all
                top = basePosition.top;
                left = basePosition.left;
                width = basePosition.width;
                height = basePosition.height;
            } else {
                // otherwise do some corrections for the opposite side. The element is positioned
                // from the top-left, so these need to be tweaked based on what's already calculated
                if (boundingLimits.bottom === 0 && position.bottom > 0) {
                    top = top + position.bottom;
                }

                if (boundingLimits.right === 0 && position.right < 0) {
                    left = left - position.right;
                }
            }

            // calculate some properties for an animation
            // start is where the element is now, these are set in a transform
            var start = {
                top: position.top,
                left: position.left,
                width: position.width,
                "max-width": position.width,
                height: position.height,
                "max-height": position.height,
            };

            // final is where we want to end up, the bounds we calculated earlier
            var final = {
                top: top,
                left: left,
                width: width,
                "max-width": width,
                height: height,
                "max-height": height,
            };

            // doesn't matter if we say whether or not it's zoomed now
            this._isZoomed = this.getCurrentScale() > 1;
            if (!this._isZoomed) {
                this.emit(EVENT_ZOOM_END);
            }

            // if we calculated the same dimensions, no animation, but if not, have one
            if (_.isEqual(start, final)) {
                this._completeWithoutAnimation(final);
            } else {
                this._completeWithAnimation(start, final);
            }
        },

        _completeWithoutAnimation: function(final) {
            style.setStyles(
                this._zoomObject,
                _.extend(
                    {
                        transform: "translate3d(0,0,0)",
                        "transform-origin": "50% 50%",
                    },
                    final
                )
            );
        },

        _completeWithAnimation: function(start, final) {
            style.setStyles(
                this._zoomObject,
                _.extend(
                    {
                        transform: "translate3d(0,0,0)",
                        "transform-origin": "50% 50%",
                    },
                    start
                )
            );

            requestAnimationFrame(
                function() {
                    this._zoomObject.classList.add("zoom--snapping");
                    style.setStyles(this._zoomObject, final);
                    events.doOnce(
                        this._zoomObject,
                        browser.transitionEndEvent,
                        function() {
                            this._zoomObject.classList.remove("zoom--snapping");
                        }.bind(this)
                    );
                }.bind(this)
            );
        },
    }
);
